// SharedMonitorView — public read-only view of a Discrepancy Monitor.
// Mounted at /share/m/:token; consumes /api/public/share/monitor/:token.
// Anyone with the token can see this — no authentication.
//
// Strict scope: this page renders date-level results only. It NEVER renders or
// fetches: bundle-level discrepancy, advertiser breakdown, partner API configs,
// alert email, or any edit/run controls. If we later add features here, they
// must come through the scrubbed backend payload — keep this file dumb.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// ── Formatters (mirror the authed view's style; kept local to avoid coupling) ──
function fmtInt(n) {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtCurrency(n) {
  const v = Number(n) || 0;
  return '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPct(n) {
  if (n === null || n === undefined) return '—';
  return (Number(n) || 0).toFixed(2) + '%';
}
function severityOf(pct) {
  const a = Math.abs(Number(pct) || 0);
  return a > 20 ? 'critical' : a > 5 ? 'warning' : 'ok';
}
function severityColor(s) {
  // 'ok' must NOT be --cyan: that is now FLAME, so a healthy result rendered
  // as a bright-orange badge on the public share page. Flame is brand, never status.
  return s === 'critical' ? 'var(--red)' : s === 'warning' ? 'var(--amber)' : 'var(--green)';
}

function SeverityBadge({ severity }) {
  const c = severityColor(severity);
  const label = severity === 'critical' ? 'Critical' : severity === 'warning' ? 'Warning' : 'OK < 5%';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', fontSize: '10px', fontWeight: 700,
      color: c, borderRadius: '999px', background: `color-mix(in srgb, ${c} 13%, transparent)`, textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}>
      ● {label}
    </span>
  );
}

// ── Daily-rows table — reused for both basic and free-form sub-tables ──
// ── Monthly grouping (mirrors the authed dashboard's MonthlyDateTable) ──
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function monthLabel(ym) { const [y,m] = ym.split('-'); return `${MONTH_NAMES[parseInt(m,10)-1]} ${y}`; }
const pctDiff = (a, b) => { const base = Math.max(Math.abs(a), Math.abs(b)); return base === 0 ? 0 : ((a - b) / base) * 100; };

function groupRowsByMonth(rows, keys) {
  const { leftImps, rightImps, leftRev, rightRev } = keys;
  const byMonth = new Map();
  for (const r of rows || []) {
    const ym = String(r.date).slice(0, 7);
    if (!byMonth.has(ym)) byMonth.set(ym, { ym, label: monthLabel(ym), rows: [],
      sumLeftImps:0, sumRightImps:0, sumLeftRev:0, sumRightRev:0, critical:0, warning:0, ok:0 });
    const g = byMonth.get(ym);
    g.rows.push(r);
    g.sumLeftImps  += Number(r[leftImps])  || 0;
    g.sumRightImps += Number(r[rightImps]) || 0;
    g.sumLeftRev   += Number(r[leftRev])   || 0;
    g.sumRightRev  += Number(r[rightRev])  || 0;
    const sev = r.severity || severityOf(r.impPct);
    if (sev === 'critical') g.critical++; else if (sev === 'warning') g.warning++; else g.ok++;
  }
  for (const g of byMonth.values()) {
    g.impDiff = g.sumLeftImps - g.sumRightImps;
    g.revDiff = g.sumLeftRev - g.sumRightRev;
    g.impPct  = pctDiff(g.sumLeftImps, g.sumRightImps);
    g.revPct  = pctDiff(g.sumLeftRev, g.sumRightRev);
    g.severity = g.critical > 0 ? 'critical' : g.warning > 0 ? 'warning' : 'ok';
  }
  // Newest month first
  return [...byMonth.values()].sort((a, b) => b.ym.localeCompare(a.ym));
}

function DailyRowsTable({ rows, leftLabel, rightLabel, leftField, rightField }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [open, setOpen]         = useState(() => new Set()); // collapsed by default
  const toggle = ym => setOpen(prev => { const n = new Set(prev); n.has(ym) ? n.delete(ym) : n.add(ym); return n; });

  if (!rows || rows.length === 0) {
    return <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>No data.</div>;
  }
  const li = `${leftField}Imps`, ri = `${rightField}Imps`;
  const lr = `${leftField}Rev`,  rr = `${rightField}Rev`;
  const filtered = rows.filter(r => (!dateFrom || r.date >= dateFrom) && (!dateTo || r.date <= dateTo));
  const groups = groupRowsByMonth(filtered, { leftImps: li, rightImps: ri, leftRev: lr, rightRev: rr });
  const colorImp = v => v > 0 ? 'var(--amber)' : 'var(--green)';

  return (
    <div>
      {/* Date filter */}
      <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap', marginBottom:'10px', fontSize:'11px', color:'var(--text-muted)' }}>
        <span style={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' }}>Filter dates</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          style={{ padding:'3px 6px', fontSize:'11px', width:'150px', background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:'4px', color:'var(--text-primary)', colorScheme:'dark' }}/>
        <span>→</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          style={{ padding:'3px 6px', fontSize:'11px', width:'150px', background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:'4px', color:'var(--text-primary)', colorScheme:'dark' }}/>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            style={{ padding:'3px 10px', fontSize:'11px', background:'var(--bg-elevated)', border:'1px solid var(--border-bright)', borderRadius:'4px', color:'var(--text-primary)', cursor:'pointer' }}>Clear</button>
        )}
      </div>

      {groups.length === 0 ? (
        <div style={{ textAlign:'center', padding:'16px', fontSize:'12px', color:'var(--text-muted)' }}>No data in this date range.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>{leftLabel} Imps</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>{rightLabel} Imps</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Imp Δ</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Imp %</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>{leftLabel} Rev</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>{rightLabel} Rev</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Rev Δ</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Rev %</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                const isOpen = open.has(g.ym);
                const sevColor = severityColor(g.severity);
                return (
                  <React.Fragment key={g.ym}>
                    {/* Month banner row */}
                    <tr onClick={() => toggle(g.ym)}
                      style={{ cursor:'pointer', background:'var(--bg-elevated)', borderTop:'2px solid var(--border)' }}>
                      <td style={{ padding:'9px 8px 9px 10px', fontWeight:800, borderLeft:`3px solid ${sevColor}` }}>
                        <span style={{
                          display:'inline-flex', alignItems:'center', justifyContent:'center',
                          width:'22px', height:'22px', borderRadius:'6px', marginRight:'10px',
                          background: isOpen ? 'var(--cyan)' : 'transparent',
                          border: isOpen ? 'none' : '1.5px solid var(--cyan)',
                          color: isOpen ? 'var(--on-accent)' : 'var(--cyan)',
                          fontSize:'13px', fontWeight:900, verticalAlign:'middle',
                        }}>{isOpen ? '▾' : '▸'}</span>
                        <span style={{ fontSize:'13px', color:'var(--text-primary)' }}>{g.label}</span>
                        <span style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:600, marginLeft:'6px' }}>· {g.rows.length} {g.rows.length===1?'day':'days'}</span>
                      </td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800 }}>{fmtInt(g.sumLeftImps)}</td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800 }}>{fmtInt(g.sumRightImps)}</td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800, color:colorImp(g.impDiff) }}>{g.impDiff>0?'+':''}{fmtInt(g.impDiff)}</td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800, color:sevColor }}>{fmtPct(g.impPct)}</td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800 }}>{fmtCurrency(g.sumLeftRev)}</td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800 }}>{fmtCurrency(g.sumRightRev)}</td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800, color:colorImp(g.revDiff) }}>{g.revDiff>0?'+':''}{fmtCurrency(g.revDiff)}</td>
                      <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:800, color:severityColor(severityOf(g.revPct)) }}>{fmtPct(g.revPct)}</td>
                      <td style={{ padding:'9px 10px' }}><SeverityBadge severity={g.severity}/></td>
                    </tr>

                    {/* MONTH TOTAL strip */}
                    {isOpen && (
                      <tr style={{ background:'var(--bg-base)' }}>
                        <td colSpan={10} style={{ padding:'6px 14px 6px 23px', borderLeft:`3px solid ${sevColor}` }}>
                          <span style={{ display:'inline-flex', gap:'18px', flexWrap:'wrap', fontSize:'11px', color:'var(--text-muted)', fontFamily:'monospace' }}>
                            <span>MONTH TOTAL</span>
                            <span>Imp Δ <b style={{ color: colorImp(g.impDiff) }}>{g.impDiff>0?'+':''}{fmtInt(g.impDiff)}</b> <span style={{ color:sevColor }}>({fmtPct(g.impPct)})</span></span>
                            <span>Rev Δ <b style={{ color: colorImp(g.revDiff) }}>{g.revDiff>0?'+':''}{fmtCurrency(g.revDiff)}</b> <span style={{ color:severityColor(severityOf(g.revPct)) }}>({fmtPct(g.revPct)})</span></span>
                          </span>
                        </td>
                      </tr>
                    )}

                    {/* DAILY BREAKDOWN divider */}
                    {isOpen && (
                      <tr style={{ background:'var(--bg-base)' }}>
                        <td colSpan={10} style={{ padding:'3px 14px 3px 23px', fontSize:'9px', letterSpacing:'1.5px', color:'var(--text-muted)', textTransform:'uppercase', borderBottom:'1px solid var(--border)' }}>
                          Daily breakdown
                        </td>
                      </tr>
                    )}

                    {/* Daily rows */}
                    {isOpen && g.rows.map((r, ri2) => {
                      const sev = r.severity || severityOf(r.impPct);
                      const impDiff = (Number(r[li]) || 0) - (Number(r[ri]) || 0);
                      const revDiff = (Number(r[lr]) || 0) - (Number(r[rr]) || 0);
                      return (
                        <tr key={ri2} style={{ fontSize:'11px', background: sev !== 'ok' ? `color-mix(in srgb, ${severityColor(sev)} 7%, transparent)` : (ri2 % 2 ? 'color-mix(in srgb, var(--text-primary) 3%, transparent)' : undefined) }}>
                          <td style={{ padding:'6px 8px 6px 45px', color:'var(--text-muted)' }}>{r.date}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace' }}>{fmtInt(r[li])}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace' }}>{fmtInt(r[ri])}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace', color: colorImp(impDiff) }}>{impDiff>0?'+':''}{fmtInt(impDiff)}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace', fontWeight:700, color: severityColor(sev) }}>{fmtPct(r.impPct)}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace' }}>{fmtCurrency(r[lr])}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace' }}>{fmtCurrency(r[rr])}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace', color: colorImp(revDiff) }}>{revDiff>0?'+':''}{fmtCurrency(revDiff)}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'monospace', fontWeight:700, color: severityColor(severityOf(r.revPct)) }}>{fmtPct(r.revPct)}</td>
                          <td style={{ padding:'6px 10px' }}><SeverityBadge severity={sev}/></td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ summary, leftLabel, rightLabel, leftField, rightField, title }) {
  if (!summary) return null;
  const sev = summary.severity || severityOf(summary.impPct);
  const li = `total${leftField}Imps`, ri = `total${rightField}Imps`;
  const lr = `total${leftField}Rev`,  rr = `total${rightField}Rev`;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>{title}</h3>
        <SeverityBadge severity={sev}/>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Impressions</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>{leftLabel}</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtInt(summary[li])}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>{rightLabel}</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtInt(summary[ri])}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 700, marginTop: '4px', color: severityColor(sev) }}>
            <span>Δ {fmtPct(summary.impPct)}</span>
            <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>{summary.impDiff > 0 ? '+' : ''}{fmtInt(summary.impDiff)}</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Revenue</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>{leftLabel}</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtCurrency(summary[lr])}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>{rightLabel}</span>
            <span style={{ fontFamily: 'monospace' }}>{fmtCurrency(summary[rr])}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 700, marginTop: '4px', color: severityColor(severityOf(summary.revPct)) }}>
            <span>Δ {fmtPct(summary.revPct)}</span>
            <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>{summary.revDiff > 0 ? '+' : ''}{fmtCurrency(summary.revDiff)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SharedMonitorView() {
  const { token } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/public/share/monitor/${encodeURIComponent(token)}`)
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(e => {
        if (cancelled) return;
        setError(e.response?.data?.error || 'Could not load this share link.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const wrapStyle = {
    position: 'fixed', inset: 0, overflowY: 'auto',
    background: 'var(--bg-base)', color: 'var(--text-primary)',
    padding: '24px', fontFamily: 'Inter, -apple-system, sans-serif',
    zIndex: 1, // sits above any inherited app shell that might overlap
  };
  if (loading) return <div style={wrapStyle}><div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>Loading…</div></div>;
  if (error)   return <div style={wrapStyle}><div style={{ maxWidth: '600px', margin: '60px auto', padding: '20px', background: 'var(--bg-card)', border: '1px solid color-mix(in srgb, var(--red) 40%, transparent)', borderRadius: '8px', textAlign: 'center' }}>
    <div style={{ fontSize: '14px', color: 'var(--red)', fontWeight: 700, marginBottom: '6px' }}>Link unavailable</div>
    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{error}</div>
  </div></div>;

  const { monitor, result } = data || {};
  const isFreeform = result?.type === 'freeform';

  return (
    <div style={wrapStyle}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '14px', borderBottom: '1px solid var(--border)', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <a href="https://www.ignitorads.co" target="_blank" rel="noopener noreferrer"
               style={{
                 display: 'inline-flex', alignItems: 'center', gap: '6px',
                 padding: '6px 12px', marginBottom: '10px',
                 background: 'linear-gradient(135deg, color-mix(in srgb, var(--purple) 18%, transparent), color-mix(in srgb, var(--purple) 18%, transparent))',
                 border: '1px solid color-mix(in srgb, var(--purple) 45%, transparent)',
                 borderRadius: '20px',
                 fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
                 color: 'var(--purple)', textDecoration: 'none',
               }}>
              ⚡ Built by IGNITORADS AdTech Hub
            </a>
            <h1 style={{ fontSize: '24px', fontWeight: 800, margin: 0 }}>{monitor?.name}</h1>
            {monitor?.dateRange && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Data range: {monitor.dateRange.from} → {monitor.dateRange.to}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
            {/* Pill 1 — Read-only badge (cyan) */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px',
              background: 'color-mix(in srgb, var(--cyan) 12%, transparent)',
              border: '1px solid color-mix(in srgb, var(--cyan) 40%, transparent)',
              borderRadius: '20px',
              fontSize: '11px', fontWeight: 700, color: 'var(--cyan)',
              letterSpacing: '0.5px', textTransform: 'uppercase',
            }}>
              👁 Read-only Shared View
            </div>
            {/* Pill 2 — Get this CTA (purple gradient) */}
            <a href="https://www.ignitorads.co" target="_blank" rel="noopener noreferrer"
               style={{
                 display: 'inline-flex', alignItems: 'center', gap: '6px',
                 padding: '6px 12px',
                 background: 'linear-gradient(135deg, color-mix(in srgb, var(--purple) 25%, transparent), color-mix(in srgb, var(--purple) 25%, transparent))',
                 border: '1px solid color-mix(in srgb, var(--purple) 55%, transparent)',
                 borderRadius: '20px',
                 fontSize: '11px', fontWeight: 700, color: 'var(--purple)',
                 textDecoration: 'none',
               }}>
              ✨ Get this for your stack → ignitorads.co
            </a>
          </div>
        </div>

        {/* Free-form: two comparisons */}
        {isFreeform ? (
          <>
            {result.supplyVsAdServer && (
              <>
                <SummaryCard
                  summary={result.supplyVsAdServer.summary}
                  leftLabel={(monitor.supplyPartnerNames || [])[0] || 'Supply'}
                  rightLabel={monitor.adServerName}
                  leftField="Supply" rightField="AdServer"
                  title={`Supply → Ad Server (${(monitor.supplyPartnerNames || []).join(', ') || 'Supply'} vs ${monitor.adServerName})`}
                />
                <DailyRowsTable
                  rows={result.supplyVsAdServer.rows}
                  leftLabel={(monitor.supplyPartnerNames || [])[0] || 'Supply'}
                  rightLabel={monitor.adServerName}
                  leftField="supply" rightField="adServer"
                />
              </>
            )}
            {result.adServerVsDemand && (
              <div style={{ marginTop: '20px' }}>
                <SummaryCard
                  summary={result.adServerVsDemand.summary}
                  leftLabel={monitor.adServerName}
                  rightLabel={(monitor.partnerNames || [])[0] || 'Demand'}
                  leftField="AdServer" rightField="Demand"
                  title={`Ad Server → Demand (${monitor.adServerName} vs ${(monitor.partnerNames || []).join(', ') || 'Demand'})`}
                />
                <DailyRowsTable
                  rows={result.adServerVsDemand.rows}
                  leftLabel={monitor.adServerName}
                  rightLabel={(monitor.partnerNames || [])[0] || 'Demand'}
                  leftField="adServer" rightField="demand"
                />
              </div>
            )}
          </>
        ) : (
          <>
            <SummaryCard
              summary={result?.summary}
              leftLabel={monitor?.adServerName || 'Ad Server'}
              rightLabel={(monitor?.partnerNames || []).join(', ') || 'Partners'}
              leftField="AdServer" rightField="Partner"
              title={`${monitor?.adServerName || 'Ad Server'} vs ${(monitor?.partnerNames || []).join(', ') || 'Partners'}`}
            />
            <DailyRowsTable
              rows={result?.rows}
              leftLabel={monitor?.adServerName || 'Ad Server'}
              rightLabel={(monitor?.partnerNames || []).join(', ') || 'Partners'}
              leftField="adServer" rightField="partner"
            />
          </>
        )}

        {/* Footer */}
        <div style={{ marginTop: '32px', paddingTop: '20px', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>
            Never miss a day to monitor Daily discrepancies.
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            IgnitorAds AdTech Hub gives you 11+ more tools to ease your Ad Operations.
          </div>
          <a href="https://www.ignitorads.co" target="_blank" rel="noopener noreferrer"
             style={{ display: 'inline-block', padding: '10px 20px', background: 'var(--accent)', color: 'var(--on-accent)', fontSize: '13px', fontWeight: 700, borderRadius: '6px', textDecoration: 'none' }}>
            Try IgnitorAds Today →
          </a>
          <div style={{ marginTop: '14px', fontSize: '10px', color: 'var(--border-bright)' }}>
            ignitorads.co · Built for Ad Ops teams
          </div>
        </div>
      </div>
    </div>
  );
}
